import { editorContextStore } from "@/contexts/editor-context";
import {
  CHECKOUT_SESSION_STATE,
  CheckoutSessionState,
} from "@/core/common/types/checkout-session-state";
import { debugError, debugLog } from "@/core/utils/print-utilts";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * Hook that initializes Rewardful and captures referral ID
 */
export function useRewardfulInitEffect() {
  const setRewardfulReferral = editorContextStore((state) => state.setRewardfulReferral);

  useEffect(() => {
    const initRewardful = () => {
      if (typeof window.rewardful !== "function") {
        debugError("[useRewardfulInitEffect] Rewardful not available");
        return;
      }

      try {
        window.rewardful("ready", () => {
          try {
            const referral = window.Rewardful?.referral;
            debugLog("[useRewardfulInitEffect] Rewardful referral: ", referral);
            setRewardfulReferral(referral);
          } catch (error) {
            debugError("[useRewardfulInitEffect] Error handling rewardful ready: ", error);
          }
        });
      } catch (error) {
        debugError("[useRewardfulInitEffect] Error initializing rewardful: ", error);
      }
    };

    // Initialize with small delay to ensure script has loaded
    const timeoutId = window.setTimeout(initRewardful, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [setRewardfulReferral]);
}

/**
 * Hook that tracks successful checkout conversions for Rewardful
 */
export function useRewardfulConvertEffect() {
  const [searchParams] = useSearchParams();
  const user = editorContextStore((state) => state.user);
  const checkoutSessionState = searchParams.get(CHECKOUT_SESSION_STATE);

  useEffect(() => {
    if (!checkoutSessionState) {
      return;
    }

    debugLog(`[useRewardfulConvertEffect] Handle checkout session ${checkoutSessionState}`);

    if (!user?.email || checkoutSessionState !== CheckoutSessionState.Success) {
      return;
    }

    try {
      if (typeof window.rewardful !== "function") {
        debugError("[useRewardfulConvertEffect] Rewardful not available");
        return;
      }

      window.rewardful("convert", { email: user.email });
    } catch (error) {
      debugError(
        `[useRewardfulConvertEffect] Error handling checkout session state ${checkoutSessionState}: `,
        error,
      );
    }
  }, [user, checkoutSessionState]);
}
